import { inject } from 'vue';
import type { VueCookies } from 'vue-cookies';

import { useFischer } from '@/composables/fischerPlugin';
import type { FischerPlugin } from '@/lib/fischerPlugin';
import type { SitecoreRendering } from '@/sitecoreState';

// This is the (unmodified) logic copied from vue-cookies.
const vueCookiesParse = (value: string | null | undefined) => {
  if (value?.substring(0, 1) === '{' && value.substring(value.length - 1, value.length) === '}') {
    try {
      value = JSON.parse(value);
    } catch (_e) {
      return value;
    }
  }
  return value;
};

const parseRenderingCookies = (cookiesStr: string): Record<string, string> => {
  const result: Record<string, string> = {};
  cookiesStr.split(';').forEach((pair) => {
    const eqIdx = pair.indexOf('=');
    if (eqIdx > 0) {
      const name = pair.slice(0, eqIdx).trim();
      const value = pair.slice(eqIdx + 1).trim();
      if (name) {
        result[name] = value;
      }
    }
  });
  return result;
};

export const getCookieFunction: (
  key: string,
  fischer: FischerPlugin,
  cookies: VueCookies,
  rendering: SitecoreRendering | null,
) => any = (
  key: string,
  fischer: FischerPlugin,
  cookies: VueCookies,
  rendering: SitecoreRendering | null,
) => {
  if (IS_SSR) {
    const ssrCookies = fischer.ssrCookies();
    const dataRaw = ssrCookies?.[key];
    if (dataRaw) {
      return vueCookiesParse(decodeURIComponent(dataRaw));
    }

    const renderingCookiesStr = rendering?.cookies;
    if (renderingCookiesStr && typeof renderingCookiesStr === 'string') {
      const renderingCookies = parseRenderingCookies(renderingCookiesStr);
      const renderingRaw = renderingCookies[key];
      if (renderingRaw) {
        return vueCookiesParse(decodeURIComponent(renderingRaw));
      }
    }

    return null;
  }

  return cookies.get(key);
};

export const useSsrCookies = () => {
  const cookies = inject<VueCookies>('$cookies');
  const rendering = inject<SitecoreRendering>('rendering');
  const fischer = useFischer();

  if (!cookies) {
    throw new Error('missing injection $cookies');
  }

  const getCookie = (key: string) => getCookieFunction(key, fischer, cookies, rendering ?? null);

  return { getCookie };
};
